import React from 'react';
import { Sparkles, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, X, Loader2 } from 'lucide-react';
import { useProjectStore } from '../../lib/stores/projectStore';

export const TaskCreationProgress: React.FC = () => {
  const progress = useProjectStore(state => state.bulkCreateProgress);
  const setCollapsed = useProjectStore(state => state.setBulkCreateCollapsed);
  const dismiss = useProjectStore(state => state.dismissBulkCreateProgress);

  if (!progress) return null;

  const percent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  // Collapsed view
  if (progress.collapsed) {
    return (
      <div className="fixed bottom-4 right-4 z-[9999] animate-in slide-in-from-bottom-4">
        <button
          onClick={() => setCollapsed(false)}
          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          {progress.active ? (
            <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
          ) : progress.result?.success ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {progress.active
              ? `Task AI ${progress.current}/${progress.total}`
              : progress.result?.success
                ? `${progress.result.created} task create`
                : 'Errore creazione'
            }
          </span>
          <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-80 animate-in slide-in-from-bottom-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {progress.active ? (
              <Sparkles className="h-4 w-4 text-purple-500" />
            ) : progress.result?.success ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-500" />
            )}
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {progress.active ? 'Creazione task AI' : progress.result?.success ? 'Task create!' : 'Errore'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCollapsed(true)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Riduci"
            >
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            </button>
            {!progress.active && (
              <button
                onClick={dismiss}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Chiudi"
              >
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {progress.active ? (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate">
                {progress.projectName}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">
                  {progress.current}/{progress.total}
                </span>
              </div>
            </>
          ) : progress.result ? (
            <p className={`text-sm ${progress.result.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {progress.result.message}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
